/**
 * Helper utilities for common localization patterns
 */
import { msg, str } from '@lit/localize';
/**
 * Format a count with singular/plural handling
 * NOTE: This function cannot use msg() with dynamic keys due to lit-localize limitations.
 * Use simplePlural() instead or define specific msg() calls in your component.
 *
 * @deprecated Use simplePlural() or define msg() calls directly
 */
// export function formatCount(
//   count: number,
//   singularKey: string,
//   pluralKey: string
// ): string {
//   // Cannot use dynamic keys with lit-localize
//   throw new Error('Use simplePlural() or define msg() calls directly in components');
// }
/**
 * Simple pluralization for English-like languages
 * Adds 's' for plural, nothing for singular
 *
 * @param count - The number to check
 * @param word - The word to pluralize (in English)
 * @returns Localized plural string
 *
 * @example
 * simplePlural(1, 'item')  // "1 item"
 * simplePlural(3, 'item')  // "3 items"
 */
export function simplePlural(count, word) {
    return msg(str `${count} ${word}${count !== 1 ? 's' : ''}`);
}
/**
 * Common action button labels
 * Reusable translations for standard actions
 */
export const CommonActions = {
    confirm: () => msg('Confirm', { id: 'common.action.confirm', desc: 'Confirm button' }),
    cancel: () => msg('Cancel', { id: 'common.action.cancel', desc: 'Cancel button' }),
    delete: () => msg('Delete', { id: 'common.action.delete', desc: 'Delete button' }),
    edit: () => msg('Edit', { id: 'common.action.edit', desc: 'Edit button' }),
    save: () => msg('Save', { id: 'common.action.save', desc: 'Save button' }),
    add: () => msg('Add', { id: 'common.action.add', desc: 'Add button' }),
    remove: () => msg('Remove', { id: 'common.action.remove', desc: 'Remove button' }),
    export: () => msg('Export', { id: 'common.action.export', desc: 'Export button' }),
    import: () => msg('Import', { id: 'common.action.import', desc: 'Import button' }),
    search: () => msg('Search', { id: 'common.action.search', desc: 'Search button' }),
    next: () => msg('Next', { id: 'common.action.next', desc: 'Next button' }),
    previous: () => msg('Previous', { id: 'common.action.previous', desc: 'Previous button' }),
    validate: () => msg('Validate', { id: 'common.action.validate', desc: 'Validate button' }),
    back: () => msg('Back', { id: 'common.action.back', desc: 'Back button' }),
    close: () => msg('Close', { id: 'common.action.close', desc: 'Close button' }),
    refresh: () => msg('Refresh', { id: 'common.action.refresh', desc: 'Refresh button' }),
};
/**
 * Common state labels
 * Reusable translations for application states
 */
export const CommonStates = {
    loading: () => msg('Loading...', { id: 'common.state.loading', desc: 'Loading state' }),
    active: () => msg('Active', { id: 'common.state.active', desc: 'Active state' }),
    inactive: () => msg('Inactive', { id: 'common.state.inactive', desc: 'Inactive state' }),
    error: () => msg('Error', { id: 'common.state.error', desc: 'Error state' }),
    success: () => msg('Success', { id: 'common.state.success', desc: 'Success state' }),
};
/**
 * Common messages
 * Reusable translations for standard messages
 */
export const CommonMessages = {
    noData: () => msg('No data available', { id: 'common.message.noData', desc: 'No data message' }),
    noDescription: () => msg('No description available', { id: 'common.message.noDescription', desc: 'No description message' }),
    noNetwork: () => msg('No network selected', { id: 'common.message.noNetwork', desc: 'No network selected message' }),
    noPoi: () => msg('No POI available', { id: 'common.message.noPoi', desc: 'No POI message' }),
    noResults: () => msg('No results found', { id: 'common.message.noResults', desc: 'No search results' }),
};
/**
 * Common form labels
 * Reusable translations for form fields
 */
export const CommonFormLabels = {
    name: () => msg('Name', { id: 'form.label.name', desc: 'Name field label' }),
    description: () => msg('Description', { id: 'form.label.description', desc: 'Description field label' }),
    address: () => msg('Address', { id: 'form.label.address', desc: 'Address field label' }),
    type: () => msg('Type', { id: 'form.label.type', desc: 'Type field label' }),
    file: () => msg('File', { id: 'form.label.file', desc: 'File field label' }),
    email: () => msg('Email', { id: 'form.label.email', desc: 'Email field label' }),
    phone: () => msg('Phone', { id: 'form.label.phone', desc: 'Phone field label' }),
};
/**
 * Common form placeholders
 * Reusable translations for form input placeholders
 */
export const CommonPlaceholders = {
    search: () => msg('Search...', { id: 'form.placeholder.search', desc: 'Search input placeholder' }),
    enterName: () => msg('Enter name', { id: 'form.placeholder.enterName', desc: 'Name input placeholder' }),
    enterNumber: () => msg('Enter a number...', { id: 'form.placeholder.enterNumber', desc: 'Number input placeholder' }),
    selectOption: () => msg('Select an option', { id: 'form.placeholder.selectOption', desc: 'Select input placeholder' }),
};
/**
 * Format a user-facing error message
 * NOTE: Cannot use dynamic keys with lit-localize. Define msg() calls directly in components.
 *
 * @deprecated Define msg() calls directly in components
 */
// export function formatError(errorKey: string, context?: Record<string, unknown>): string {
//   // Cannot use dynamic keys with lit-localize
//   throw new Error('Define msg() calls directly in components');
// }
/**
 * Format a success message
 * NOTE: Cannot use dynamic keys with lit-localize. Define msg() calls directly in components.
 *
 * @deprecated Define msg() calls directly in components
 */
// export function formatSuccess(successKey: string, context?: Record<string, unknown>): string {
//   // Cannot use dynamic keys with lit-localize
//   throw new Error('Define msg() calls directly in components');
// }
//# sourceMappingURL=helpers.js.map